import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Input, Field } from "@/components/admin/ui";
import { OPTS, SPECIAL_FEATURES } from "@/utils/strollerAttrs";

const Sel = ({ label, field, a, set, testId }) => (
  <Field label={label}>
    <select
      value={a[field] ?? ""}
      onChange={(e) => set(field, e.target.value === "" ? null : (field === "seats" ? Number(e.target.value) : e.target.value))}
      className="w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg"
      data-testid={testId || `sqa-${field}`}
    >
      <option value="">—</option>
      {OPTS[field].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </Field>
);

const YN = ({ label, field, a, set }) => (
  <label className="flex items-center gap-2 cursor-pointer text-sm text-[#F6F1E4] py-1" data-testid={`sqa-${field}`}>
    <input type="checkbox" checked={!!a[field]} onChange={(e) => set(field, e.target.checked)} className="accent-[#FFD166]" />
    {label}
  </label>
);

const Num = ({ label, field, a, set, step = "1" }) => (
  <Field label={label}>
    <Input type="number" min="0" step={step} value={a[field] ?? ""}
      onChange={(e) => set(field, e.target.value === "" ? null : Number(e.target.value))} data-testid={`sqa-${field}`} />
  </Field>
);

const Txt = ({ label, field, a, set, placeholder }) => (
  <Field label={label}>
    <Input value={a[field] ?? ""} onChange={(e) => set(field, e.target.value)} placeholder={placeholder} data-testid={`sqa-${field}`} />
  </Field>
);

const Group = ({ title, children }) => (
  <div className="rounded-xl border border-[#FFD166]/15 bg-black/20 p-3">
    <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFD166] mb-2">{title}</div>
    {children}
  </div>
);

/* Hidden admin-only section that powers the stroller quiz + comparison tool.
   NEVER rendered in the public product description. */
export const StrollerAttributesEditor = ({ value, onChange }) => {
  const [open, setOpen] = useState(!!value?.include_in_quiz || !!value?.include_in_comparison);
  const a = value || {};
  const set = (field, v) => onChange({ ...a, [field]: v });
  const toggleFeat = (f) => {
    const cur = a.special_features || [];
    set("special_features", cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]);
  };

  return (
    <div className="mt-6 border-t border-[#FFD166]/15 pt-5" data-testid="stroller-attrs-section">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left" data-testid="stroller-attrs-toggle">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#FFD166]">Stroller Quiz Attributes</div>
          <p className="text-[11px] text-[#B9C8D5] mt-1">Hidden, admin-only. Powers the stroller quiz and comparison tool — never shown in the public description.</p>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-[#FFD166] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#FFD166] shrink-0" />}
      </button>

      {open && (
        <div className="mt-4 space-y-3">
          <Group title="Recommendation settings">
            <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1">
              <YN label="Include in stroller quiz" field="include_in_quiz" a={a} set={set} />
              <YN label="Include in comparison tool" field="include_in_comparison" a={a} set={set} />
              <YN label="Featured recommendation" field="featured_recommendation" a={a} set={set} />
              <Num label="Recommendation priority (1–10, small tie-breaker only)" field="recommendation_priority" a={a} set={set} />
            </div>
            <Txt label="Admin notes" field="admin_notes" a={a} set={set} placeholder="Internal notes…" />
          </Group>

          <Group title="Basic capacity">
            <div className="grid sm:grid-cols-3 gap-3">
              <Sel label="Number of children" field="seats" a={a} set={set} />
              <Sel label="Stroller type" field="stroller_type" a={a} set={set} />
              <Sel label="Seating style" field="seating_style" a={a} set={set} />
              <Num label="Min recommended age (months)" field="min_age_months" a={a} set={set} />
              <Num label="Max recommended age (years)" field="max_age_years" a={a} set={set} step="0.5" />
              <Num label="Max child weight per seat (lbs)" field="max_child_weight_lbs" a={a} set={set} />
              <Num label="Max combined weight (lbs, optional)" field="max_combined_weight_lbs" a={a} set={set} />
            </div>
          </Group>

          <Group title="Stroller weight & size">
            <div className="grid sm:grid-cols-2 gap-3">
              <Num label="Actual weight (lbs)" field="weight_lbs" a={a} set={set} step="0.1" />
              <Sel label="Weight category" field="weight_category" a={a} set={set} />
              <Txt label="Unfolded dimensions (L × W × H)" field="unfolded_size" a={a} set={set} placeholder='e.g. 33 × 20 × 41 in' />
            </div>
            <p className="text-[10px] text-[#B9C8D5] mt-2">
              Disney allows strollers up to 31&Prime; wide × 52&Prime; long — listing the unfolded size helps guests confirm it's park-approved.
            </p>
          </Group>

          <Group title="Fold">
            <div className="grid sm:grid-cols-2 gap-3">
              <Sel label="Fold difficulty" field="fold_difficulty" a={a} set={set} />
              <Txt label="Folded size" field="folded_size" a={a} set={set} placeholder='e.g. 31 × 20 × 12 in' />
            </div>
            <div className="grid sm:grid-cols-3 gap-x-4 mt-1">
              <YN label="One-hand fold" field="one_hand_fold" a={a} set={set} />
              <YN label="Compact fold" field="compact_fold" a={a} set={set} />
              <YN label="Self-standing fold" field="self_standing_fold" a={a} set={set} />
            </div>
          </Group>

          <Group title="Child comfort">
            <div className="grid sm:grid-cols-2 gap-3">
              <Sel label="Overall child comfort" field="comfort" a={a} set={set} />
              <Sel label="Seat padding" field="seat_padding" a={a} set={set} />
            </div>
            <div className="grid sm:grid-cols-2 gap-x-4 mt-1">
              <YN label="Footrest" field="footrest" a={a} set={set} />
              <YN label="Adjustable footrest" field="adjustable_footrest" a={a} set={set} />
            </div>
          </Group>

          <Group title="Recline & napping">
            <div className="grid sm:grid-cols-2 gap-3">
              <Sel label="Recline type" field="recline" a={a} set={set} />
              <Sel label="Nap friendly" field="nap_friendly" a={a} set={set} />
            </div>
          </Group>

          <Group title="Canopy">
            <Sel label="Canopy size" field="canopy_size" a={a} set={set} />
            <div className="grid sm:grid-cols-2 gap-x-4 mt-1">
              <YN label="Extendable canopy" field="extendable_canopy" a={a} set={set} />
              <YN label="Good sun coverage" field="sun_coverage" a={a} set={set} />
            </div>
          </Group>

          <Group title="Storage">
            <Sel label="Storage basket size" field="basket_size" a={a} set={set} />
            <YN label="Easy basket access" field="easy_basket_access" a={a} set={set} />
          </Group>

          <Group title="Ride & terrain">
            <div className="grid sm:grid-cols-2 gap-3">
              <Sel label="Best terrain" field="best_terrain" a={a} set={set} />
              <Sel label="Ride quality" field="ride_quality" a={a} set={set} />
              <Sel label="Suspension" field="suspension" a={a} set={set} />
              <Sel label="Maneuverability" field="maneuverability" a={a} set={set} />
            </div>
          </Group>

          <Group title="Theme park use">
            <Sel label="Disney / theme park suitability" field="park_suitability" a={a} set={set} />
            <div className="grid sm:grid-cols-3 gap-x-4 mt-1">
              <YN label="Easy for crowded parks" field="crowd_friendly" a={a} set={set} />
              <YN label="Easy on/off transportation" field="transport_friendly" a={a} set={set} />
              <YN label="Compact enough for travel" field="travel_compact" a={a} set={set} />
            </div>
          </Group>

          <Group title="Parent convenience">
            <div className="grid sm:grid-cols-3 gap-x-4">
              <YN label="Parent cup holder" field="parent_cup_holder" a={a} set={set} />
              <YN label="Parent console" field="parent_console" a={a} set={set} />
              <YN label="Child snack tray available" field="snack_tray_available" a={a} set={set} />
              <YN label="Large storage priority" field="large_storage_priority" a={a} set={set} />
              <YN label="Easy steering priority" field="easy_steering_priority" a={a} set={set} />
            </div>
            <Txt label="Car-seat compatibility (optional)" field="car_seat_compat" a={a} set={set} placeholder="e.g. Nuna PIPA series" />
          </Group>

          <Group title="Special features">
            <div className="flex flex-wrap gap-1.5">
              {SPECIAL_FEATURES.map(([v, l]) => {
                const on = (a.special_features || []).includes(v);
                return (
                  <button key={v} type="button" onClick={() => toggleFeat(v)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-[#FFD166] text-[#1a0a03] border-[#FFD166]" : "border-[#FFD166]/25 text-[#B9C8D5] hover:border-[#FFD166]/60"}`}
                    data-testid={`sqa-feat-${v}`}>
                    {l}
                  </button>
                );
              })}
            </div>
            <div className="mt-2">
              <Txt label="Any standout features (short text)" field="standout_features" a={a} set={set} placeholder="e.g. Tallest canopy in our fleet" />
            </div>
          </Group>
        </div>
      )}
    </div>
  );
};
